import React, { useEffect, useMemo, useRef, useState } from "react";
import Popup from "./Popup";
import {
  caseStatus,
  DATE_TIME_FORMAT,
  fileNameType,
  testPlanStatusColor,
  testPlanStatusLabel,
  testPlanStatus as testPlanStatusValue,
} from "app/constants";
import moment from "moment";
import { commonServices, projectServices } from "app/services/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import messages from "../../../../services/api/messages";
import PrimaryBtn from "../../../UI/Button/PrimaryBtn";
import { Dialog, DialogActions, DialogContent } from "@mui/material";
import { useFormik } from "formik";
import InputText from "../../../UI/Form/Input/InputText";
import { formatIndex, getBaseName, getFileExtension } from "../../../../utils";

function TestPlanCell({
  width,
  testPlanStatus,
  testPlanInfo,
  caseInfo,
  setCaseInfo,
  highlight = false,
  setDownloading,
}) {
  const [anchor, setAnchor] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const isSubmitted = testPlanStatus === testPlanStatusValue.submitted;
  const isNotStarted = testPlanStatus === testPlanStatusValue.notStarted;
  const isNone = testPlanStatus === testPlanStatusValue.none;
  const isConfirmed = testPlanStatus === testPlanStatusValue.confirmed;
  const isRejected = testPlanStatus === testPlanStatusValue.rejected;

  const disabledChangeStatus = useMemo(() => {
    return (
      (caseInfo?.caseStatus === caseStatus.completed ||
        caseInfo?.caseStatus === caseStatus.lost) &&
      !isSubmitted
    );
  }, [caseInfo, isSubmitted]);

  const ref = useRef();

  useEffect(() => {
    if (highlight) {
      ref?.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [highlight]);

  const handleLeftClickTestPlan = () => {
    if (testPlanInfo?.fileInfo) {
      const caseId = caseInfo?.caseId || "";
      const fileType = fileNameType["testPlan"];
      const customerName = caseInfo?.customer?.companyNameKanji || "";
      const timeStamp = moment().format("YYYYMMDDHHmmss");
      const fileName = `${fileType}_${caseId}_${customerName}`;

      const files = Array.isArray(testPlanInfo.fileInfo) ? testPlanInfo.fileInfo : [testPlanInfo.fileInfo];

      setDownloading(true);

      Promise.all(
        files.map((file, index) =>
          new Promise((resolve) => {
            setTimeout(() => {
              const name =  file.fileName
                ? getBaseName(file.fileName)
                : `${fileName}_${formatIndex(index + 1)}_${timeStamp}`;

              const extension = getFileExtension(file);
              if (extension === "zip") {
                commonServices
                  .downloadWithFileId(file._id, extension, name)
                  .finally(() => resolve());
              } else {
                commonServices
                  .downloadPDFWithFileId(file._id, name)
                  .finally(() => resolve());
              }
            }, index * 500);
          })
        )
      ).finally(() => setDownloading(false));
    }
  };

  const handleRightClickTestPlan = (e) => {
    e.preventDefault();
    if (disabledChangeStatus) return;
    if (isRejected) return;
    if (!isNone) {
      setAnchor(e.target);
    }
  };

  const handleUpdateTestPlanStatus = (value) => {
    projectServices
      .updateStatus({
        id: caseInfo._id,
        statusType: "testPlanStatus",
        value: values.comment ? testPlanStatusValue.rejected : value,
        comment: values.comment,
      })
      .then((res) => {
        const newCaseInfo = res?.data?.result;
        if (!newCaseInfo._id) {
          throw Error("Update error!");
        }
        setAnchor(null);
        setOpenModal(false);
        setCaseInfo({
          ...caseInfo,
          testPlanStatus: newCaseInfo?.testPlanStatus,
          updatePlanTime: newCaseInfo?.updatePlanTime,
          caseStatus: newCaseInfo?.caseStatus,
        });
        toast.success(messages.update(true));
      })
      .catch(() => {
        toast.error(messages.callApiFail);
      });
  };

  const showModal = () => {
    setOpenModal(true);
    setAnchor(null);
    resetForm();
  };

  const closeModal = () => {
    setOpenModal(false);
    setAnchor(null);
  };

  const {
    handleSubmit,
    resetForm,
    errors,
    values,
    setFieldValue,
    touched,
    handleBlur,
  } = useFormik({
    initialValues: {
      comment: "",
    },
    onSubmit: handleUpdateTestPlanStatus,
    enableReinitialize: true,
  });
  return (
    <>
      <div
        ref={ref}
        className={`h-full border border-[#B3B3B3] px-2 py-4 shrink-0 ${
          highlight && "bg-yellow-100"
        }`}
        style={{
          width: width,
        }}
      >
        <p className="text-sm leading-[18px] tracking-[.2em] truncate text-center">
          <button
            style={{
              color: testPlanStatusColor[testPlanStatus],
              cursor: "pointer",
            }}
            onClick={handleLeftClickTestPlan}
            onContextMenu={handleRightClickTestPlan}
          >
            {testPlanStatusLabel[testPlanStatus]}
          </button>
        </p>
      </div>

      <Popup
        id={`test-plan-${testPlanInfo?._id}`}
        anchor={anchor}
        open={Boolean(anchor)}
      >
        {isConfirmed && (
          <div className="flex gap-2">
            <PrimaryBtn
              text="承認"
              onClick={() =>
                handleUpdateTestPlanStatus(testPlanStatusValue.submitted)
              }
            />
            <PrimaryBtn text="差し戻し" onClick={showModal} />
            <PrimaryBtn text="戻る" onClick={() => setAnchor(null)} />
          </div>
        )}

        {isSubmitted && (
          <div className="flex flex-col">
            <button
              className="bg-[#c7c2c2] w-7 h-7 rounded-full ml-auto flex items-center justify-center"
              onClick={() => setAnchor(null)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <p className="flex gap-2">
              最終更新日:{" "}
              {moment(
                caseInfo?.updatePlanTime || testPlanInfo?.updatedAt
              ).format(DATE_TIME_FORMAT)}
            </p>
          </div>
        )}
        {isNotStarted && !disabledChangeStatus && (
          <div className="flex gap-2">
            <PrimaryBtn
              text="無に変更"
              onClick={() =>
                handleUpdateTestPlanStatus(testPlanStatusValue.none)
              }
            />
            <PrimaryBtn text="戻る" onClick={() => setAnchor(null)} />
          </div>
        )}
      </Popup>

      <Dialog open={openModal} onClose={() => setOpenModal(false)}>
        <DialogContent
          sx={{
            pl: "16px",
          }}
        >
          <div className="mb-4">コメント</div>
          <InputText
            name="comment"
            value={values.comment}
            onChange={(value) => setFieldValue("comment", value)}
            handleBlur={handleBlur}
            errors={errors.comment}
            touched={touched.comment}
          />
        </DialogContent>

        <DialogActions>
          <PrimaryBtn text={"キャンセル"} onClick={closeModal} />
          <PrimaryBtn text={"送信"} onClick={handleSubmit} />
        </DialogActions>
      </Dialog>
    </>
  );
}

export default TestPlanCell;
